/**
 * ¿En qué cuadro/corral está parado el usuario? Punto-en-polígono contra los
 * elementos del mapa (offline: Dexie en web, SQLite en mobile). Las coordenadas
 * NUNCA salen del dispositivo: sólo viaja la ubicación resuelta.
 * Spec: aiChatBot/docs/superpowers/specs/2026-10-16-contexto-de-pantalla-design.md §4.3
 */
import { puntoEnPoligono, distanciaPuntoSegmentoM, type Coordenada } from '../../../utils/geoUtils';
import type { ElementoMapa } from '../../storage/interfaces/IElementoMapaRepository';
import type { PosicionContext } from './types';

export const GPS_MAX_ACCURACY_M = 500;
export const GPS_MAX_AGE_MS = 10 * 60_000;
export const GPS_LIMITE_M = 50;

export interface LecturaGps { lng: number; lat: number; accuracy: number; timestamp: number }

type Poligono = ElementoMapa & { tipo: 'CUADRO' | 'CORRAL'; ubicacion_id: string };

const anillo = (e: ElementoMapa): Coordenada[] =>
  (e.geometria?.type === 'Polygon' ? (e.geometria.coordinates[0] as Coordenada[]) : []) || [];

function distanciaAlBordeM(p: Coordenada, ring: Coordenada[]): number {
  let min = Infinity;
  for (let i = 0; i + 1 < ring.length; i++) min = Math.min(min, distanciaPuntoSegmentoM(p, ring[i], ring[i + 1]).distM);
  return min;
}

export function resolverPosicion(lectura: LecturaGps, elementos: ElementoMapa[], now = Date.now()): PosicionContext | null {
  if (!(lectura.accuracy <= GPS_MAX_ACCURACY_M)) return null;
  if (now - lectura.timestamp > GPS_MAX_AGE_MS) return null;
  const p: Coordenada = [lectura.lng, lectura.lat];
  const polys = elementos.filter((e): e is Poligono =>
    (e.tipo === 'CUADRO' || e.tipo === 'CORRAL') && !!e.ubicacion_id && !(e as { is_deleted?: boolean }).is_deleted && anillo(e).length >= 3);
  const contienen = polys.filter((e) => puntoEnPoligono(p[0], p[1], anillo(e)));
  if (!contienen.length) return null;
  // Corral antes que cuadro: un corral suele estar adentro de un cuadro → gana el más chico.
  const principal = contienen.find((e) => e.tipo === 'CORRAL') || contienen[0];
  const margen = Math.max(GPS_LIMITE_M, lectura.accuracy);
  // "Cerca de" sólo un polígono que NO contiene el punto (el cuadro que contiene al corral no cuenta).
  let cerca: { nombre: string; d: number } | null = null;
  for (const e of polys) {
    if (contienen.includes(e)) continue;
    const d = distanciaAlBordeM(p, anillo(e));
    if (d < margen && (!cerca || d < cerca.d)) cerca = { nombre: e.nombre, d };
  }
  return {
    ubicacion_id: principal.ubicacion_id,
    nombre: principal.nombre,
    tipo: principal.tipo,
    cerca_de: cerca ? cerca.nombre : null,
    precision_m: Math.round(lectura.accuracy),
    hace_s: Math.max(0, Math.round((now - lectura.timestamp) / 1000)),
  };
}
